import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { agentApi } from "@/services/agentApi";
import type { AgentSupportContacts } from "@/types/user";
import { SupportContactPanel } from "../../../../shared/src/components/support-contacts/SupportContactPanel";
import "./assignedContacts.css";

/** Key the entire request state so another account's contacts never render. */
export function AssignedContactPanel() {
  const { user } = useAuth();
  if (!user?.agentProfile) return null;
  const identity = `${user.id}:${user.agentProfile.id}:${user.agentProfile.parentAgentId || ""}`;
  return <ContactLookup key={identity} />;
}

function ContactLookup() {
  const [data, setData] = useState<AgentSupportContacts | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState("loading");
    setData(null);
    void agentApi
      .getSupportContacts(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setState("ready");
      })
      .catch(() => {
        if (!controller.signal.aborted) setState("error");
      });
    return () => controller.abort();
  }, [retry]);

  const general = data?.state === "GENERAL_SUPPORT";
  const subtitle =
    data?.state === "ASSIGNED" && data.market
      ? `${data.market.name} · ${data.source === "COUNTRY" ? "Country contacts" : "Regional contacts"}`
      : "Support for your agency";

  return (
    <SupportContactPanel
      className={`assigned-contacts${state === "error" ? " assigned-contacts--error" : ""}`}
      busy={state === "loading"}
      description={general ? "General support" : subtitle}
      contacts={
        state === "ready" && data && data.state !== "FALLBACK_UNAVAILABLE"
          ? data.contacts
          : []
      }
    >
      {state === "loading" && (
        <p className="assigned-contacts__loading" role="status">
          <Loader2 size={16} aria-hidden="true" />
          Loading contacts…
        </p>
      )}
      {state === "error" && (
        <div role="alert">
          <p className="assigned-contacts__error-copy">
            Unable to load support contacts.
          </p>
          <button
            type="button"
            className="assigned-contacts__retry"
            onClick={() => setRetry((value) => value + 1)}
          >
            Retry
          </button>
        </div>
      )}
      {state === "ready" && data?.state === "FALLBACK_UNAVAILABLE" && (
        <p className="assigned-contacts__unavailable">
          Support contact details are not available yet.
        </p>
      )}
    </SupportContactPanel>
  );
}
